/**
 * Shared ECharts options for MicroEAI Fairness enterprise plots.
 * Used by Fairness.tsx (Bias tab) and FairnessCard (Dashboard).
 */
import type { EChartsCoreOption } from 'echarts/core';
import {
  SS_THRESHOLD,
  PRR_THRESHOLD,
  RISK_GREEN_MAX,
  RISK_AMBER_MAX,
  countByState,
  type ComplianceRow,
  type RiskDict
} from './fairnessEnterprise.utils';

export function getPlot1EligibilityGateOption(
  complianceRows: ComplianceRow[],
  auditRows: ComplianceRow[] | null
): EChartsCoreOption | null {
  const labels = ['ELIGIBLE', 'PARTIAL', 'NONE', 'EXCLUDED'];
  const c = countByState(complianceRows);
  const a = auditRows && auditRows.length > 0 ? countByState(auditRows) : null;
  const w = 0.35;

  return {
    tooltip: { trigger: 'axis' },
    legend: { data: a ? ['Compliance', 'Audit'] : ['Compliance'], bottom: 0 },
    grid: { left: '8%', right: '6%', bottom: '18%', top: '12%', containLabel: true },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: { color: '#6c757d' },
      axisLine: { lineStyle: { color: '#dcdcdc' } }
    },
    yAxis: {
      type: 'value',
      name: 'Attribute Count',
      axisLabel: { color: '#6c757d' },
      axisLine: { lineStyle: { color: '#dcdcdc' } },
      splitLine: { lineStyle: { color: '#f0f0f0' } }
    },
    series: [
      {
        name: 'Compliance',
        type: 'bar',
        data: [c.ELIGIBLE, c.PARTIAL, c.NONE, c.EXCLUDED],
        barWidth: a ? w : '55%',
        itemStyle: { color: '#0288d1', borderRadius: [4, 4, 0, 0] },
        barGap: 0
      },
      ...(a
        ? [
            {
              name: 'Audit',
              type: 'bar',
              data: [a.ELIGIBLE, a.PARTIAL, a.NONE, a.EXCLUDED],
              barWidth: w,
              itemStyle: { color: '#7b1fa2', borderRadius: [4, 4, 0, 0] },
              barGap: 0
            }
          ]
        : [])
    ],
    title: {
      text: `Plot 1 — Eligibility Gate (Compliance vs Audit)\nEligibility requires SS≥${SS_THRESHOLD} and PRR≥${PRR_THRESHOLD}`,
      left: 'center',
      top: 0,
      textStyle: { fontSize: 12, fontWeight: 'bold' }
    }
  };
}

export function getPlot2EvidenceBalanceOption(complianceRows: ComplianceRow[]): EChartsCoreOption | null {
  const withEvidence = complianceRows.filter(r => r.State !== 'NONE');
  if (withEvidence.length === 0) return null;

  const attrs = withEvidence.map(r => r.Attribute);
  const N_A = withEvidence.map(r => r.N_A);
  const N_B = withEvidence.map(r => r.N_B);
  const PRR = withEvidence.map(r => (Number.isNaN(r.PRR) ? 0 : r.PRR));
  const w = 0.35;

  return {
    tooltip: { trigger: 'axis' },
    legend: {
      data: ['Group A (N_A)', 'Group B (N_B)', 'PRR', `PRR threshold (${PRR_THRESHOLD})`],
      bottom: 0
    },
    grid: { left: '8%', right: '8%', bottom: '22%', top: '18%', containLabel: true },
    xAxis: {
      type: 'category',
      data: attrs,
      axisLabel: { color: '#6c757d', rotate: attrs.length > 4 ? 25 : 0 },
      axisLine: { lineStyle: { color: '#dcdcdc' } }
    },
    yAxis: [
      {
        type: 'value',
        name: 'Evidence Count',
        axisLabel: { color: '#6c757d' },
        axisLine: { lineStyle: { color: '#dcdcdc' } },
        splitLine: { lineStyle: { color: '#f0f0f0' } }
      },
      {
        type: 'value',
        name: 'PRR',
        min: 0,
        max: 1,
        axisLabel: { color: '#6c757d' },
        axisLine: { show: false },
        splitLine: { show: false }
      }
    ],
    series: [
      {
        name: 'Group A (N_A)',
        type: 'bar',
        data: N_A,
        barWidth: w,
        itemStyle: { color: '#1976d2', borderRadius: [4, 4, 0, 0] },
        barGap: 0
      },
      {
        name: 'Group B (N_B)',
        type: 'bar',
        data: N_B,
        barWidth: w,
        itemStyle: { color: '#ffb300', borderRadius: [4, 4, 0, 0] },
        barGap: 0
      },
      {
        name: 'PRR',
        type: 'line',
        yAxisIndex: 1,
        data: PRR,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: { color: '#6a1b9a', width: 2 },
        itemStyle: { color: '#6a1b9a' }
      },
      {
        name: `PRR threshold (${PRR_THRESHOLD})`,
        type: 'line',
        yAxisIndex: 1,
        data: attrs.map(() => PRR_THRESHOLD),
        lineStyle: { type: 'dashed', color: '#e53935', width: 1 },
        symbol: 'none'
      }
    ],
    title: {
      text: `Plot 2 — Group Evidence Balance (Compliance)\nSS≥${SS_THRESHOLD} and PRR≥${PRR_THRESHOLD} required for ELIGIBLE`,
      left: 'center',
      top: 0,
      textStyle: { fontSize: 12, fontWeight: 'bold' }
    }
  };
}

export function getPlot3PolicyAndRiskOption(
  complianceRows: ComplianceRow[],
  auditRows: ComplianceRow[],
  riskDict: RiskDict
): EChartsCoreOption | null {
  const labels = ['ELIGIBLE', 'PARTIAL', 'NONE', 'EXCLUDED'];
  const c = countByState(complianceRows);
  const a = auditRows.length > 0 ? countByState(auditRows) : { ELIGIBLE: 0, PARTIAL: 0, NONE: 0, EXCLUDED: 0 };
  const cvals = [c.ELIGIBLE, c.PARTIAL, c.NONE, c.EXCLUDED];
  const avals = [a.ELIGIBLE, a.PARTIAL, a.NONE, a.EXCLUDED];
  const risk = riskDict.Risk;
  const band = riskDict.Band;
  const w = 0.35;

  return {
    title: [
      {
        text: `Plot 3 — Policy Impact + MicroEAI Fairness Risk\nGovernance=${
          riskDict.GovernanceState
        } | Risk=${risk.toFixed(2)} (${band}) | Required=${riskDict.Required} Eligible=${
          riskDict.Eligible
        } Unfulfilled=${riskDict.Unfulfilled} | Readiness=${riskDict.Readiness.toFixed(2)}`,
        left: 'center',
        top: 0,
        textStyle: { fontSize: 11, fontWeight: 'bold' }
      }
    ],
    grid: [
      { left: '8%', right: '52%', bottom: '15%', top: '20%', containLabel: true },
      { left: '58%', right: '8%', bottom: '15%', top: '20%', containLabel: true }
    ],
    tooltip: { trigger: 'axis' },
    xAxis: [
      {
        gridIndex: 0,
        type: 'category',
        data: labels,
        axisLabel: { color: '#6c757d' },
        axisLine: { lineStyle: { color: '#dcdcdc' } }
      },
      {
        gridIndex: 1,
        type: 'category',
        data: ['Risk'],
        axisLabel: { color: '#6c757d' },
        axisLine: { lineStyle: { color: '#dcdcdc' } }
      }
    ],
    yAxis: [
      {
        gridIndex: 0,
        type: 'value',
        name: 'Attribute Count',
        axisLabel: { color: '#6c757d' },
        axisLine: { lineStyle: { color: '#dcdcdc' } },
        splitLine: { lineStyle: { color: '#f0f0f0' } }
      },
      {
        gridIndex: 1,
        type: 'value',
        min: 0,
        max: 1,
        name: 'Risk (0–1)',
        axisLabel: { color: '#6c757d' },
        axisLine: { lineStyle: { color: '#dcdcdc' } },
        splitLine: { lineStyle: { color: '#f0f0f0' } }
      }
    ],
    series: [
      {
        name: 'Compliance',
        type: 'bar',
        data: cvals,
        barWidth: w,
        itemStyle: { color: '#0288d1', borderRadius: [4, 4, 0, 0] },
        barGap: 0,
        xAxisIndex: 0,
        yAxisIndex: 0
      },
      {
        name: 'Audit',
        type: 'bar',
        data: avals,
        barWidth: w,
        itemStyle: { color: '#7b1fa2', borderRadius: [4, 4, 0, 0] },
        barGap: 0,
        xAxisIndex: 0,
        yAxisIndex: 0
      },
      {
        name: 'Risk',
        type: 'bar',
        data: [risk],
        barWidth: '40%',
        itemStyle: {
          color: risk <= RISK_GREEN_MAX ? '#4caf50' : risk <= RISK_AMBER_MAX ? '#ff9800' : '#f44336',
          borderRadius: [4, 4, 0, 0]
        },
        xAxisIndex: 1,
        yAxisIndex: 1,
        markLine: {
          symbol: 'none',
          data: [
            {
              yAxis: RISK_GREEN_MAX,
              name: `Green ≤ ${RISK_GREEN_MAX}`,
              lineStyle: { type: 'dashed', color: '#4caf50' }
            },
            {
              yAxis: RISK_AMBER_MAX,
              name: `Amber ≤ ${RISK_AMBER_MAX}`,
              lineStyle: { type: 'dashed', color: '#ff9800' }
            }
          ]
        }
      }
    ],
    legend: {
      data: ['Compliance', 'Audit', 'Risk'],
      bottom: 0
    }
  };
}
